import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react';

// Async setters from a previous identity cannot repopulate the new identity.
export function useScopedBadgeState<T>(scope: string, initialValue: T) {
  const identity = useMemo(() => ({ scope }), [scope]);
  const [state, setState] = useState({ identity, value: initialValue });
  const currentScope = useRef(identity);
  currentScope.current = identity;
  const initial = useRef(initialValue);
  initial.current = initialValue;
  const setValue = useCallback((next: SetStateAction<T>) => {
    if (currentScope.current !== identity) return;
    setState((previous) => {
      if (currentScope.current !== identity) return previous;
      const value = previous.identity === identity ? previous.value : initial.current;
      return { identity, value: typeof next === 'function' ? (next as (value: T) => T)(value) : next };
    });
  }, [identity]);
  return [state.identity === identity ? state.value : initial.current, setValue] as const;
}

export function useBadgeRequestGuard(scope: string) {
  const state = useMemo(() => ({ active: true, requests: new Map<string, number>() }), [scope]);
  const current = useRef(state);
  current.current = state;
  useEffect(() => {
    state.active = true;
    return () => { state.active = false; state.requests.forEach((value, key) => state.requests.set(key, value + 1)); };
  }, [state]);
  return useCallback((channel: string) => {
    const id = (state.requests.get(channel) || 0) + 1;
    state.requests.set(channel, id);
    return () => current.current === state && state.active && state.requests.get(channel) === id;
  }, [state]);
}
